/**
 * The Zoho CRM preset's seam (piece 31; decision 8380): the CRM's modules, a
 * custom module's fields and related lists, its records and each record's
 * related contacts, in our own shapes. 🔨 Our types in, our types out: the
 * adapter validates every answer into these, an absent field reading as empty,
 * and no CRM field name passes it except the API names the module's own fields
 * list answered, which a read keeps as its field map.
 *
 * The authority is Zoho's own CRM API v8 reference, read 2026-10-26: Scopes,
 * Modules API, Fields Metadata, Related Lists Metadata, Get Records and Get
 * Related Records (https://www.zoho.com/crm/developer/docs/api/v8/).
 */
import type { BooksAddress, BooksContact, BooksContactPerson } from './booksSource';
import { zohoBooksHosts, type ZohoBooksDataCentre } from './zohoBooksSettings';

/**
 * 🔒 The five read scopes Connect with Zoho CRM asks for (decision 8380), each
 * from the reference's Scopes page: the custom modules' records, the Contacts
 * module's records, and the modules list, the fields list and the related lists
 * under settings. 🔨 Nothing is asked for that writes.
 */
export const ZOHO_CRM_SCOPES: readonly string[] = [
  'ZohoCRM.modules.custom.READ',
  'ZohoCRM.modules.contacts.READ',
  'ZohoCRM.settings.modules.READ',
  'ZohoCRM.settings.fields.READ',
  'ZohoCRM.settings.related_lists.READ',
];

/** 🔒 The two Settings rows the preset holds (decision 8380): the sealed refresh token Connect writes, and the custom module's API name. */
export const ZOHO_CRM_REFRESH_TOKEN_KEY = 'zohoCrm.refreshToken';
export const ZOHO_CRM_MODULE_KEY = 'zohoCrm.manufacturerModule';
export const ZOHO_CRM_SETTING_KEYS: readonly string[] = [ZOHO_CRM_REFRESH_TOKEN_KEY, ZOHO_CRM_MODULE_KEY];
export const ZOHO_CRM_SEALED_KEYS: readonly string[] = [ZOHO_CRM_REFRESH_TOKEN_KEY];

/** The preset's routes, relative to the API root. */
export const ZOHO_CRM_IMPORT_PATH = '/import/zoho-crm';
export const ZOHO_CRM_CONNECT_PATH = `${ZOHO_CRM_IMPORT_PATH}/connect`;
export const ZOHO_CRM_CALLBACK_PATH = `${ZOHO_CRM_IMPORT_PATH}/callback`;
export const ZOHO_CRM_DISCONNECT_PATH = `${ZOHO_CRM_IMPORT_PATH}/disconnect`;
export const ZOHO_CRM_MODULES_PATH = `${ZOHO_CRM_IMPORT_PATH}/modules`;
export const ZOHO_CRM_MODULE_PATH = `${ZOHO_CRM_IMPORT_PATH}/module`;
export const ZOHO_CRM_RUNS_PATH = `${ZOHO_CRM_IMPORT_PATH}/runs`;

/** 🔒 The custom modules a person chooses among where the modules list names no Manufacture module, and the one chosen (decision 8380). */
export interface ZohoCrmModuleChoice {
  modules: { apiName: string; label: string }[];
  chosen: string | null;
}

/** The query word the Import page reads on its return from Zoho's consent page. */
export const ZOHO_CRM_OUTCOME_PARAM = 'zohoCrm';

/** The redirect address registered in Zoho's console beside the Books one: the application's address, then `/api/import/zoho-crm/callback`. */
export function zohoCrmRedirectUri(appBaseUrl: string): string {
  return `${appBaseUrl.replace(/\/+$/, '')}/api${ZOHO_CRM_CALLBACK_PATH}`;
}

/** Zoho's authorization address for the CRM's scopes, on the data center the Books rows name, with the same offline grant. */
export function zohoCrmAuthorizationUrl(input: { dataCentre: ZohoBooksDataCentre; clientId: string; redirectUri: string; state: string }): string {
  const query = new URLSearchParams({
    scope: ZOHO_CRM_SCOPES.join(','),
    client_id: input.clientId,
    response_type: 'code',
    access_type: 'offline',
    prompt: 'consent',
    redirect_uri: input.redirectUri,
    state: input.state,
  });
  return `${zohoBooksHosts(input.dataCentre).accounts}/oauth/v2/auth?${query.toString()}`;
}

/**
 * 🔒 The CRM API's base (the reference's OAuth pages: *use the value in the
 * `api_domain` key to make API calls to Zoho CRM*): the token answer's domain
 * where it names an https Zoho host, else the data center's `zohoapis` host.
 */
export function zohoCrmApiBase(apiDomain: string | null | undefined, dataCentre: ZohoBooksDataCentre): string {
  const named = typeof apiDomain === 'string' ? apiDomain.trim().replace(/\/+$/, '') : '';
  if (/^https:\/\/[a-z0-9.-]+\.zohoapis\.[a-z.]+$/i.test(named) || /^https:\/\/[a-z0-9.-]+\.zohocloud\.ca$/i.test(named)) return `${named}/crm/v8`;
  const books = zohoBooksHosts(dataCentre).api;
  return books.replace(/\/books\/v3$/, '/crm/v8');
}

/** The Import page's return after Connect: the import screen with the outcome, and a reason code where it was refused. */
export function zohoCrmImportReturn(appBaseUrl: string, outcome: 'connected' | 'refused', reason: string | null = null): string {
  const query = new URLSearchParams({ [ZOHO_CRM_OUTCOME_PARAM]: outcome });
  if (outcome === 'refused' && reason && /^[A-Za-z]{1,30}$/.test(reason)) query.set('reason', reason);
  return `${appBaseUrl.replace(/\/+$/, '')}/admin/import?${query.toString()}`;
}

// ---- The seam's values ----

/** One module the modules list answers, as the preset reads it. */
export interface CrmModule {
  apiName: string;
  /** The module's display name, `module_name`, with its singular and plural labels. */
  label: string;
  singular: string;
  plural: string;
  /** True where `generated_type` is `custom`. */
  custom: boolean;
  apiSupported: boolean;
}

/** One field of a module, as the fields list answers it. */
export interface CrmField {
  apiName: string;
  label: string;
  dataType: string;
}

/** One related list of a module, with the API name of the module it lists. */
export interface CrmRelatedList {
  apiName: string;
  module: string;
}

/**
 * 🔒 Where a read finds each of a manufacturer's facts in the custom module
 * (decision 8380): the field API names its own fields list answered, matched by
 * their labels, since the reference fixes no name for a custom module's EIN or
 * website. A fact with no field reads as empty.
 */
export interface CrmFieldMap {
  name: string;
  website: string | null;
  phone: string | null;
  taxNumber: string | null;
  street: string | null;
  city: string | null;
  state: string | null;
  postalCode: string | null;
  country: string | null;
  /** The related list naming the Contacts module, which a manufacturer's people are read from; null where the module has none. */
  contactsList: string | null;
}

/** One page of a CRM list: its rows, its number, and whether another follows (`info.more_records`). */
export interface CrmPage<T> {
  rows: T[];
  page: number;
  hasMore: boolean;
}

/** One contact a manufacturer's related list names, as a person here would be read. */
export interface CrmContact {
  id: string;
  firstName: string;
  lastName: string;
  title: string | null;
  email: string | null;
  phone: string | null;
  mobile: string | null;
  department: string | null;
}

/** One record of the custom module, as a manufacturer, with its people once they are read. */
export interface CrmManufacturer {
  id: string;
  name: string;
  website: string | null;
  phone: string | null;
  taxNumber: string | null;
  address: BooksAddress | null;
  modifiedAt: Date | null;
  /** The calendar day the record was created, as the CRM's own timestamp writes it; empty where it carries none. */
  createdOn: string | null;
  contacts: CrmContact[];
}

/**
 * 🔒 The `CrmSourceProvider` seam (decision 8380): what a Manufacturers read
 * asks of the CRM, in the reference's own order of calls.
 */
export interface CrmSourceProvider {
  /** `GET /settings/modules`. */
  modules(): Promise<CrmModule[]>;
  /** `GET /settings/fields?module=`. */
  fields(module: string): Promise<CrmField[]>;
  /** `GET /settings/related_lists?module=`. */
  relatedLists(module: string): Promise<CrmRelatedList[]>;
  /** `GET /{module}?fields=…&page=&per_page=200`, the records of a page without their people. */
  records(module: string, map: CrmFieldMap, page: number): Promise<CrmPage<CrmManufacturer>>;
  /** `GET /{module}/{id}?fields=…`, one record without its people. */
  record(module: string, map: CrmFieldMap, id: string): Promise<CrmManufacturer>;
  /** `GET /{module}/{id}/{related list}?fields=…`, a page of the record's related contacts. */
  contacts(module: string, list: string, id: string, page: number): Promise<CrmPage<CrmContact>>;
  /** Hears each rate-limit wait as it starts, as the Books source does (T1233). */
  onRateWait?(listener: ((ms: number) => void) | null): void;
}

/** The Contacts fields a related read asks for (the reference's Get Related Records: `fields` is mandatory). */
export const CRM_CONTACT_FIELDS: readonly string[] = ['First_Name', 'Last_Name', 'Full_Name', 'Title', 'Email', 'Phone', 'Mobile', 'Department'];

// ---- Reading the answers, shared by the adapter and the suites so both run one parser ----

type Row = Record<string, unknown>;

function rowOf(value: unknown): Row {
  return typeof value === 'object' && value !== null && !Array.isArray(value) ? (value as Row) : {};
}

function textOf(value: unknown): string | null {
  if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

/** A label folded for matching: lower case, letters and digits alone. */
export function foldCrmLabel(label: string): string {
  return label.toLowerCase().replace(/[^a-z0-9]/g, '');
}

/** The modules list (`modules`), each read into our shape; an entry with no API name is dropped. */
export function parseCrmModules(body: unknown): CrmModule[] {
  const list = rowOf(body).modules;
  if (!Array.isArray(list)) return [];
  return list.flatMap((raw) => {
    const row = rowOf(raw);
    const apiName = textOf(row.api_name);
    if (!apiName) return [];
    return [{
      apiName,
      label: textOf(row.module_name) ?? textOf(row.plural_label) ?? apiName,
      singular: textOf(row.singular_label) ?? '',
      plural: textOf(row.plural_label) ?? '',
      custom: row.generated_type === 'custom',
      apiSupported: row.api_supported !== false,
    }];
  });
}

/** The words a manufacturer module's names fold to: Manufacture, as the owner's org shows it, and its plurals. */
const MANUFACTURER_WORDS: ReadonlySet<string> = new Set(['manufacture', 'manufactures', 'manufacturer', 'manufacturers']);

/**
 * 🔒 Which custom module holds the manufacturers (decision 8380): the one whose
 * display name, singular or plural label folds to Manufacture or Manufacturer;
 * else the one custom module where only one exists. Where neither answers, the
 * person chooses among the custom modules, and nothing is guessed.
 */
export function pickManufacturerModule(modules: readonly CrmModule[]): { apiName: string } | { choices: CrmModule[] } {
  const custom = modules.filter((one) => one.custom && one.apiSupported);
  const named = custom.filter((one) => [one.label, one.singular, one.plural].some((name) => MANUFACTURER_WORDS.has(foldCrmLabel(name))));
  if (named.length === 1) return { apiName: named[0]!.apiName };
  if (named.length === 0 && custom.length === 1) return { apiName: custom[0]!.apiName };
  return { choices: named.length > 1 ? named : custom };
}

/** The fields list (`fields`), each read into our shape. */
export function parseCrmFields(body: unknown): CrmField[] {
  const list = rowOf(body).fields;
  if (!Array.isArray(list)) return [];
  return list.flatMap((raw) => {
    const row = rowOf(raw);
    const apiName = textOf(row.api_name);
    if (!apiName) return [];
    return [{ apiName, label: textOf(row.field_label) ?? textOf(row.display_label) ?? apiName, dataType: textOf(row.data_type) ?? '' }];
  });
}

/** The related lists (`related_lists`), each with the module it lists. */
export function parseCrmRelatedLists(body: unknown): CrmRelatedList[] {
  const list = rowOf(body).related_lists;
  if (!Array.isArray(list)) return [];
  return list.flatMap((raw) => {
    const row = rowOf(raw);
    const apiName = textOf(row.api_name);
    const module = textOf(rowOf(row.module).api_name);
    return apiName && module ? [{ apiName, module }] : [];
  });
}

/** The labels each fact is read from, folded; a field's own data type is asked first where the reference names one. */
const FACT_LABELS: Readonly<Record<Exclude<keyof CrmFieldMap, 'name' | 'contactsList'>, readonly string[]>> = {
  website: ['website', 'web', 'url', 'websiteurl', 'webaddress'],
  phone: ['phone', 'phonenumber', 'mainphone', 'telephone'],
  taxNumber: ['ein', 'einno', 'einnumber', 'taxid', 'federaltaxid', 'fein', 'taxnumber'],
  street: ['street', 'address', 'streetaddress', 'addressline1', 'billingstreet', 'mailingstreet'],
  city: ['city', 'billingcity', 'mailingcity'],
  state: ['state', 'province', 'stateprovince', 'billingstate', 'mailingstate'],
  postalCode: ['zip', 'zipcode', 'postalcode', 'postcode', 'billingcode', 'billingzip', 'mailingzip'],
  country: ['country', 'billingcountry', 'mailingcountry'],
};

/**
 * 🔒 A custom module's field map (decision 8380): the record name is the
 * module's `Name` field, or the first field labeled as a name. Each other fact
 * is the field whose folded label is on its list, or for the website and the
 * phone the first field of that data type. The related list naming Contacts is
 * where the people are read from.
 */
export function crmFieldMap(fields: readonly CrmField[], lists: readonly CrmRelatedList[]): CrmFieldMap {
  const name = fields.find((field) => field.apiName === 'Name') ?? fields.find((field) => foldCrmLabel(field.label).endsWith('name'));
  const pick = (labels: readonly string[], dataType?: string) =>
    (fields.find((field) => labels.includes(foldCrmLabel(field.label))) ?? (dataType ? fields.find((field) => field.dataType === dataType) : undefined))?.apiName ?? null;
  return {
    name: name?.apiName ?? 'Name',
    website: pick(FACT_LABELS.website, 'website'),
    phone: pick(FACT_LABELS.phone, 'phone'),
    taxNumber: pick(FACT_LABELS.taxNumber),
    street: pick(FACT_LABELS.street),
    city: pick(FACT_LABELS.city),
    state: pick(FACT_LABELS.state),
    postalCode: pick(FACT_LABELS.postalCode),
    country: pick(FACT_LABELS.country),
    contactsList: lists.find((list) => list.module === 'Contacts')?.apiName ?? null,
  };
}

/** The field API names a records read asks for, at most fifty as the reference allows, the name and the two times first. */
export function crmRecordFields(map: CrmFieldMap): string[] {
  const named = [map.name, map.website, map.phone, map.taxNumber, map.street, map.city, map.state, map.postalCode, map.country].filter((one): one is string => one !== null);
  return [...new Set([...named, 'Created_Time', 'Modified_Time'])].slice(0, 50);
}

/** An ISO time with its offset as an instant, or empty. */
function instantOf(value: unknown): Date | null {
  const raw = textOf(value);
  if (!raw) return null;
  const at = new Date(raw);
  return Number.isNaN(at.getTime()) ? null : at;
}

/** One record of the custom module as a manufacturer, through the read's field map; a record with no id or name is dropped. */
export function parseCrmManufacturer(value: unknown, map: CrmFieldMap): CrmManufacturer | null {
  const row = rowOf(value);
  const id = textOf(row.id);
  const name = textOf(row[map.name]);
  if (!id || !name) return null;
  const read = (field: string | null) => (field ? textOf(row[field]) : null);
  const street = read(map.street);
  const city = read(map.city);
  const state = read(map.state);
  const postalCode = read(map.postalCode);
  const country = read(map.country);
  const address: BooksAddress | null = street || city || state || postalCode
    ? { id: null, attention: null, lines: street ? street.split(/\r?\n/).map((line) => line.trim()).filter(Boolean) : [], city, state, postalCode, country }
    : null;
  const created = textOf(row.Created_Time);
  return {
    id, name, website: read(map.website), phone: read(map.phone), taxNumber: read(map.taxNumber), address,
    modifiedAt: instantOf(row.Modified_Time),
    createdOn: created && /^\d{4}-\d{2}-\d{2}/.test(created) ? created.slice(0, 10) : null,
    contacts: [],
  };
}

/** One related contact (the Contacts module's standard fields); one with no id is dropped, and a first name missing beside a full name is read off it. */
export function parseCrmContact(value: unknown): CrmContact | null {
  const row = rowOf(value);
  const id = textOf(row.id);
  if (!id) return null;
  const last = textOf(row.Last_Name) ?? '';
  let first = textOf(row.First_Name) ?? '';
  const full = textOf(row.Full_Name);
  if (!first && full && last && full.endsWith(last) && full.length > last.length) first = full.slice(0, full.length - last.length).trim();
  return { id, firstName: first, lastName: last, title: textOf(row.Title), email: textOf(row.Email), phone: textOf(row.Phone), mobile: textOf(row.Mobile), department: textOf(row.Department) };
}

/** A CRM list's page: its `data` rows parsed, and whether `info.more_records` says another follows. */
export function crmPageFrom<T>(body: unknown, page: number, parse: (row: unknown) => T | null): CrmPage<T> {
  const data = rowOf(body).data;
  const rows = Array.isArray(data) ? data.flatMap((raw) => {
    const parsed = parse(raw);
    return parsed === null ? [] : [parsed];
  }) : [];
  return { rows, page, hasMore: rowOf(rowOf(body).info).more_records === true };
}

/**
 * 🔒 A manufacturer as the Contacts block reads a contact (decision 8380), so
 * its matcher and its writers serve the CRM unchanged: a company flagged
 * Manufacturer, neither a customer nor a vendor, its one address as its billing
 * address and each contact a person employed there.
 */
export function crmManufacturerAsContact(manufacturer: CrmManufacturer): BooksContact {
  const persons: BooksContactPerson[] = manufacturer.contacts.map((contact, at) => ({
    id: contact.id, firstName: contact.firstName, lastName: contact.lastName, email: contact.email, phone: contact.phone, mobile: contact.mobile, title: contact.title,
    ...(contact.department ? { department: contact.department } : {}), isPrimary: at === 0,
  }));
  return {
    id: manufacturer.id, name: manufacturer.name, companyName: manufacturer.name, isCustomer: false, isVendor: false, isManufacturer: true, isActive: true,
    website: manufacturer.website, phone: manufacturer.phone, paymentTerms: null, currencyCode: null, taxNumber: manufacturer.taxNumber,
    billingAddress: manufacturer.address, shippingAddress: null, persons, unhomed: [], modifiedAt: manufacturer.modifiedAt, createdOn: manufacturer.createdOn,
  };
}
